/**
 * Client-side error reporting.
 *
 * Sends crashes and failed operations to the `log-client-error` edge function,
 * which groups them by fingerprint in `public.app_error_logs`.
 *
 * Design goals:
 * - Never throw, never block rendering.
 * - De-dupe: the same fingerprint is not re-sent more than once per minute.
 * - Batch: reports are flushed on a short timer, on page hide, and on unload
 *   (via sendBeacon) so we don't lose the last error before a navigation.
 * - Privacy: URLs are stripped of query strings server-side, no form values or
 *   message content are captured (GDPR).
 */
import { APP_VERSION } from '@/config/version';
import { addBreadcrumb, getBreadcrumbs, installInteractionBreadcrumbs } from '@/lib/breadcrumbs';

const FUNCTION_URL = 'https://tirebdkkpmeiceqesbwj.supabase.co/functions/v1/log-client-error';
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRpcmViZGtrcG1laWNlcWVzYndqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ2NDkzNjUsImV4cCI6MjA2MDIyNTM2NX0.ppMdf3K266kZqAQGinOQ7bNKNjjsVj6Jr-yJDnQb1ac';

const FLUSH_DELAY_MS = 1500;
const DEDUPE_WINDOW_MS = 60_000;
const MAX_QUEUE = 20;

export type ErrorSource =
  | 'client_boundary'
  | 'client_unhandled'
  | 'client_query'
  | 'client_mutation'
  | 'client_invoke';

export interface ReportOptions {
  source?: ErrorSource;
  severity?: 'fatal' | 'error' | 'warning';
  componentStack?: string;
  functionName?: string;
  context?: Record<string, unknown>;
}

interface QueuedReport {
  fingerprint: string;
  error_name: string;
  message: string;
  stack?: string | null;
  component_stack?: string | null;
  source: ErrorSource;
  severity: string;
  route: string;
  url: string;
  platform: string;
  app_version: string;
  function_name?: string | null;
  session_id: string;
  breadcrumbs: unknown[];
  context: Record<string, unknown>;
}

const queue: QueuedReport[] = [];
const lastSentAt = new Map<string, number>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let accessToken: string | null = null;
let installed = false;

/** Stable per-tab id so multiple errors from one session can be correlated. */
const sessionId = (() => {
  try {
    const key = 'luvall-error-session-id';
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return 'unknown-session';
  }
})();

/** Called by AuthContext so reports can be attributed to a user. */
export function setErrorReportingAuthToken(token: string | null): void {
  accessToken = token;
}

function detectPlatform(): string {
  try {
    const ua = navigator.userAgent || '';
    const standalone =
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    if (/iphone|ipad|ipod/i.test(ua)) return standalone ? 'ios-pwa' : 'ios-web';
    if (/android/i.test(ua)) return standalone ? 'android-pwa' : 'android-web';
    return standalone ? 'desktop-pwa' : 'desktop-web';
  } catch {
    return 'unknown';
  }
}

/**
 * Build a stable grouping key: variable parts (ids, numbers, urls) are
 * normalised so the same bug always lands on one row.
 */
function buildFingerprint(error: Error, source: string, route: string): string {
  const normalisedMessage = (error.message || 'unknown')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<uuid>')
    .replace(/https?:\/\/[^\s")]+/g, '<url>')
    .replace(/\d+/g, '<n>')
    .slice(0, 180);

  const topFrame =
    (error.stack || '')
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.includes('at ') || line.includes('@'))
      ?.replace(/:\d+:\d+/g, '')
      .slice(0, 120) ?? '';

  return `${source}:${error.name || 'Error'}:${normalisedMessage}:${route}:${topFrame}`;
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushReports();
  }, FLUSH_DELAY_MS);
}

async function flushReports(useBeacon = false): Promise<void> {
  if (queue.length === 0) return;
  const reports = queue.splice(0, queue.length);
  const payload = JSON.stringify({ reports });

  try {
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // sendBeacon can't set headers; the function accepts anonymous reports.
      const blob = new Blob([payload], { type: 'application/json' });
      const sent = navigator.sendBeacon(`${FUNCTION_URL}?apikey=${SUPABASE_ANON_KEY}`, blob);
      if (sent) return;
    }

    await fetch(FUNCTION_URL, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken ?? SUPABASE_ANON_KEY}`,
      },
      body: payload,
    });
  } catch {
    // Drop silently — reporting failures must not cascade.
  }
}

/** Report an error. Safe to call from anywhere; never throws. */
export function reportError(error: unknown, options: ReportOptions = {}): void {
  try {
    const err =
      error instanceof Error
        ? error
        : new Error(typeof error === 'string' ? error : JSON.stringify(error ?? 'Unknown error'));

    const route = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
    const source = options.source ?? 'client_unhandled';
    const fingerprint = buildFingerprint(err, source, route);

    const now = Date.now();
    const previous = lastSentAt.get(fingerprint);
    if (previous && now - previous < DEDUPE_WINDOW_MS) return;
    lastSentAt.set(fingerprint, now);

    if (queue.length >= MAX_QUEUE) return;

    queue.push({
      fingerprint,
      error_name: err.name || 'Error',
      message: err.message || 'Unknown error',
      stack: err.stack ?? null,
      component_stack: options.componentStack ?? null,
      source,
      severity: options.severity ?? 'error',
      route,
      url: typeof window !== 'undefined' ? window.location.href : '',
      platform: detectPlatform(),
      app_version: APP_VERSION.buildId,
      function_name: options.functionName ?? null,
      session_id: sessionId,
      breadcrumbs: getBreadcrumbs(),
      context: options.context ?? {},
    });

    addBreadcrumb('info', `error captured: ${err.name}`, { source });
    scheduleFlush();
  } catch {
    // ignore
  }
}

/**
 * Transient service-worker registration failures (preview iframes, private
 * mode, aborted installs) surface as message-less "Rejected" promises. They are
 * not app defects, so keep them out of the error log.
 */
function isServiceWorkerNoise(reason: unknown): boolean {
  try {
    const message = reason instanceof Error ? reason.message : String(reason ?? '');
    const stack = reason instanceof Error ? reason.stack ?? '' : '';
    const haystack = `${message} ${stack}`.toLowerCase();
    return haystack.includes('serviceworker') || haystack.includes('service worker');
  } catch {
    return false;
  }
}

/** Install global handlers. Call once, as early as possible. */
export function installGlobalErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  installInteractionBreadcrumbs();

  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, { source: 'client_unhandled', severity: 'fatal' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isServiceWorkerNoise(event.reason)) {
      console.warn('⚠️ Ignoring service worker rejection:', event.reason);
      return;
    }
    reportError(event.reason, { source: 'client_unhandled' });
  });


  // Flush whatever is pending before the page goes away.
  window.addEventListener('pagehide', () => void flushReports(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushReports(true);
  });
}

export { flushReports };
